<template>
	<div class="content-page content-page--impact-form">
		<!-- NOTE FOR DEV: the customer nav should be showing with the dynamic customer info pre-populated
		Customer Nav only shows when they access this screen from the customer card on Customers screen.
		-->
		<CustomerNav />
		<div class="content-page__heading" ref="contentPageHeading">
			<div class="content-page__breadcrumb-title">
				<!-- NOTE FOR DEV: Breadcrumb varies depending on how they got to this screen
				If they came from Forms link in header then Added/Edited/Copied a form = < Back to Forms
				If they came from the Customer form manager = Customers > [Customer Name] Forms > Form Designer
				See notes about max chracter limit for customer name
				-->
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">Form Designer</h2>
			</div>

			<div class="content-page__actions">
				<ButtonGroup>
					<template #container>
						<Button
							v-show="showManageCustomers"
							labelText="Manage Clients"
							:btnClick="
								() => {
									displayManageCustomersEditFlyout = true;
								}
							"
						/>

						<Button
							v-show="showPublish"
							enabled
							className="btn--primary btn--has-icon"
							iconClassName="icon-publish"
							labelText="Publish"
							:btnClick="publishForm"
							:iconBeforeLabel="true"
							:disabled="disablePublish"
						/>
					</template>
				</ButtonGroup>
				<div class="pending-changes">
					<span
						class="pending-changes__details"
						v-show="lastPublishedText"
					>
						{{ lastPublishedText }}
						{{ fullDateString(dataForm.rowModifiedDate) }}</span
					>
					<Button
						v-show="showUndoChanges"
						className="btn--link btn--small"
						labelText="Undo Pending Changes"
						:btnClick="showModalUndoChanges"
					/>
				</div>
			</div>
		</div>
		<!-- DEV NOTES: BlockNotifications component is missing the option to display the status icon based on notification requirement. -->
		<BlockContainer>
			<BlockNotifications
				title="Shared Form"
				message="This form is shared by multiple clients. Changes will be applied to the form for all assigned clients.
				If changes are unique to one client, create a copy of this form from the Forms Manager for the specific client."
				v-if="isSharedForm"
				ref="blockContainer"
			/>

			<BlockNotifications
				title="Error"
				message="There are errors on the Form Builder. Dot value is invalid and must be corrected before saving."
				:blockType="'error'"
				v-if="hasFormBuilderErrors"
				ref="blockErrorContainer"
			/>
		</BlockContainer>
		<Spinner :processor="{ loading: loadingSource }" />

		<InfiniteScroll
			ref="contentPageMain"
			:componentData="sendComponentToInfiniteScroll"
			:extraHeightInfinityScroll="extraHeightInfinityScroll"
			v-if="hasFormValue"
			@scrollEnd="handleScollEnd"
			@calculateDivMaxHeight="setCalculateDivMaxHeight"
		>
		</InfiniteScroll>

		<transition name="slide-up">
			<div
				ref="mainContentFooter"
				class="impact-form__footer"
				:class="{ 'is-fixed-footer': displayFooter }"
				v-show="hasFormValue"
			>
				<div class="impact-form__footer-container">
					<div class="impact-form__footer-message" v-if="editionMode">
						<span class="impact-form__footer-message-item">
							{{ changesOnForm }}
						</span>
					</div>
					<div class="impact-form__footer-actions">
						<ButtonGroup>
							<template #container>
								<SplitButton
									variantName="primary"
									:items="splitbuttonItems"
									:value="splitbuttonValue"
									@click="saveForm"
									:disabled="!hasFormChanges"
								/>
								<Button
									className="btn--link"
									labelText="Cancel"
									:btnClick="cancelAction"
								/>
							</template>
						</ButtonGroup>
					</div>
				</div>
			</div>
		</transition>
		<ManageCustomersEditFlyout
			v-model="displayManageCustomersEditFlyout"
			:form="dataForm"
			@toast="updateToast"
		/>
		<Modals
			type="confirmation"
			v-model="showUndoModal"
			:title="'Are you sure you want to undo pending changes?'"
			labelConfirmButton="Yes, undo pending changes"
			labelCancelButton="Cancel"
			@confirm="undoFormChanges"
			@cancel="cancel"
		/>
		<Modals
			v-model="displayConfirmationModal"
			type="confirmation"
			ref="confirmationDialogue"
			:title="'Are you sure you want to leave this page?'"
			:confirmationContent="'Unsaved changes will be lost.'"
		/>
		<FormSaveModal
			class="impact-form__footer-btns"
			v-model="showModal"
			:form="dataForm"
			@show="updateForm"
			@cancel="showModal = false"
		/>
	</div>
</template>

<script>
import CustomerNav from '@/sections/shared/customer-navegation';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import BlockContainer from '@/components/notifications/block-notifications/block-notifications-container';
import BlockNotifications from '@/components/notifications/block-notifications/block-notifications';
import SplitButton from '@/components/forms/button/split-button';
import Button from '@/components/forms/button/button';
import FormSaveModal from '@/views/global-forms/form-save-modal';
import FormDesigner from '@/mixins/forms/form-designer';
import Spinner from '@/components/visual/spinner/spinner';
import ButtonGroup from '@/components/forms/button/button-group';
import ManageCustomersEditFlyout from '@/views/global-forms/manage-customers-edit-flyout.vue';
import Modals from '@/components/shells/modal/modals';
import InfiniteScroll from '@/components/shells/infinite-scroll/infinite-scroll';
import ImpactForm from '@/views/form-designer/impact-form.vue';
import DateTimeMixin from '@/mixins/datetime-mixin';
import PageKeyNames from '@/utils/page-key-names';

export default {
	name: 'ImpactFormDesigner',
	mixins: [FormDesigner, DateTimeMixin],
	components: {
		CustomerNav,
		Breadcrumbs,
		BlockContainer,
		BlockNotifications,
		SplitButton,
		Button,
		FormSaveModal,
		Spinner,
		ButtonGroup,
		Modals,
		ManageCustomersEditFlyout,
		InfiniteScroll
	},
	mounted() {
		this.$store.commit('setCurrentPage', PageKeyNames.FORM);
	},
	destroyed() {
		this.$store.commit('setCurrentPage', null);
	},
	data() {
		return {
			displayManageCustomersEditFlyout: false,
			showUndoModal: false
		};
	},
	computed: {
		sendComponentToInfiniteScroll() {
			return {
				name: 'ImpactForm',
				mainPath: '$parent.$refs',
				parentPath: '$parent.$parent.$refs',
				component: {
					is: ImpactForm,
					props: [
						{
							form: this.dataForm,
							title: this.titleData
						}
					]
				}
			};
		}
	}
};
</script>

<style lang="scss" scoped>
/**
  * [Description]
  *
  * Impact Forms designer
  * Can use SCSS variables = TRUE
  *
  *
**/

.content-page {
	&__actions {
		display: flex;
		flex-wrap: wrap;
		justify-content: flex-end;

		.pending-changes {
			width: 100%;
			display: flex;
			flex-wrap: wrap;
			justify-content: flex-end;
			margin-top: $gutter-lg;

			@include mq($from: md) {
				display: grid;
				grid-template-columns: auto auto;
				flex-wrap: initial;
				align-items: center;
			}

			&__details {
				text-align: right;
				font-size: 12px;
				color: $neutral-2;
				margin: $gutter-md $gutter-md $gutter-sm 0;
				@include mq($from: md) {
					margin: $gutter-sm $gutter-md $gutter-sm 0;
				}
			}
		}
	}
}

.impact-form {
	&__footer {
		margin-top: $gutter-lg;
	}

	&__footer-container {
		display: flex;
		flex-direction: column-reverse;
		flex-wrap: wrap;

		@include mq($from: sm-l) {
			display: grid;
			grid-template-columns: 1fr auto;
			gap: $gutter-md; //matches header
			flex-wrap: wrap;
		}
	}

	&__footer-actions {
		display: inline-flex;
		align-items: center;
		justify-content: flex-end;
	}

	&__footer-message {
		display: inline-flex;
		justify-content: flex-end;
		align-items: center;
		font-size: 13px;

		@include mq($until: sm-l) {
			margin-top: $gutter-lg;
		}
	}
}
</style>
